import { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api } from '../services/api.ts';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { ArrowLeft, Save, Trash2, Image as ImageIcon } from 'lucide-react';
import { toast } from 'sonner';

interface TicketTier {
  type_id: string;
  name: string;
  price: number;
  description: string;
  capacity: number;
  sold?: number;
  perks: string[];
}

export default function EditEvent() {
  const navigate = useNavigate();
  const { eventId } = useParams<{ eventId: string }>();
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    artist: '',
    venue: '',
    date: '',
    end_date: '',
    category: 'concert',
    is_virtual: false,
    tags: '',
    poster_url: '',
    currency: 'USD',
    status: 'draft',
  });
  const [posterPreview, setPosterPreview] = useState<string | null>(null);
  const [ticketTiers, setTicketTiers] = useState<TicketTier[]>([]);
  const [ticketsSold, setTicketsSold] = useState(0);

  const categories = ['concert', 'festival', 'club', 'conference', 'workshop', 'other'];
  const currencies = [
    { code: 'USD', symbol: '$', name: 'US Dollar' },
    { code: 'CAD', symbol: 'C$', name: 'Canadian Dollar' },
    { code: 'EUR', symbol: '€', name: 'Euro' },
    { code: 'GBP', symbol: '£', name: 'British Pound' },
  ];

  useEffect(() => {
    if (eventId) {
      loadEvent();
    }
  }, [eventId]);

  const loadEvent = async () => {
    try {
      setIsLoading(true);
      const event = await api.getEvent(eventId!);
      
      // Format date for datetime-local input (preserve local time, don't convert to UTC)
      const formatDateForInput = (dateStr: string) => {
        if (!dateStr) return '';
        const date = new Date(dateStr);
        // Format as YYYY-MM-DDTHH:mm in local timezone
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        return `${year}-${month}-${day}T${hours}:${minutes}`;
      };

      setFormData({
        title: event.title || '',
        description: event.description || '',
        artist: event.artist || '',
        venue: event.venue || '',
        date: formatDateForInput(event.date),
        end_date: formatDateForInput(event.end_date),
        category: event.category || 'concert',
        is_virtual: event.is_virtual || false,
        tags: (event.tags || []).join(', '),
        poster_url: event.poster_url || '',
        currency: event.currency || 'USD',
        status: event.status || 'draft',
      });

      if (event.poster_url) {
        setPosterPreview(event.poster_url);
      }

      setTicketTiers(event.ticket_types || []);
      setTicketsSold(event.tickets_sold || 0);
    } catch (error: any) {
      toast.error(error.message || 'Failed to load event');
      navigate('/manage-events');
    } finally {
      setIsLoading(false);
    }
  };

  const handlePosterUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      toast.error('File size must be less than 10MB');
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      setPosterPreview(e.target?.result as string);
    };
    reader.readAsDataURL(file);

    try {
      setIsUploading(true);
      const result = await api.uploadPoster(file);
      setFormData({ ...formData, poster_url: result.url });
      toast.success('Poster uploaded successfully!');
    } catch (error: any) {
      toast.error(error.message || 'Failed to upload poster');
      setPosterPreview(null);
    } finally {
      setIsUploading(false);
    }
  };

  const handleSave = async () => {
    try {
      setIsSaving(true);
      const tags = formData.tags.split(',').map(t => t.trim()).filter(Boolean);
      
      const updateData: any = {
        title: formData.title,
        description: formData.description,
        artist: formData.artist,
        venue: formData.venue,
        location: formData.venue,
        category: formData.category,
        is_virtual: formData.is_virtual,
        currency: formData.currency,
        tags,
        poster_url: formData.poster_url,
      };

      if (formData.date) {
        updateData.date = new Date(formData.date).toISOString();
      }
      if (formData.end_date) {
        updateData.end_date = new Date(formData.end_date).toISOString();
      }

      // Include ticket tiers if they exist
      if (ticketTiers.length > 0) {
        updateData.ticket_tiers = ticketTiers.map(tier => ({
          type_id: tier.type_id,
          name: tier.name,
          price: tier.price,
          description: tier.description,
          perks: tier.perks || [],
          capacity: tier.capacity
        }));
      }

      await api.updateEvent(eventId!, updateData);
      toast.success('Event updated successfully!');
      navigate('/manage-events');
    } catch (error: any) {
      console.error('Update event error:', error);
      toast.error(error.message || 'Failed to update event');
    } finally {
      setIsSaving(false);
    }
  };

  const handlePublish = async () => {
    try {
      setIsSaving(true);
      await api.updateEvent(eventId!, { status: 'published' });
      toast.success('Event published successfully!');
      navigate('/manage-events');
    } catch (error: any) {
      toast.error(error.message || 'Failed to publish event');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!confirm('Are you sure you want to delete this event? This action cannot be undone.')) {
      return;
    }

    try {
      await api.deleteEvent(eventId!);
      toast.success('Event deleted successfully');
      navigate('/manage-events');
    } catch (error: any) {
      toast.error(error.message || 'Failed to delete event');
    }
  };

  const updateTicketTier = (index: number, field: string, value: any) => {
    const updated = [...ticketTiers];
    updated[index] = { ...updated[index], [field]: value };
    setTicketTiers(updated);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-32 w-32 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8 pb-20 md:pb-8 max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/manage-events')}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-3xl font-heading font-bold">Edit Event</h1>
            <div className="flex items-center space-x-2 mt-1">
              <Badge variant={formData.status === 'published' ? 'default' : 'secondary'}>
                {formData.status}
              </Badge>
              {ticketsSold > 0 && (
                <Badge variant="outline">{ticketsSold} tickets sold</Badge>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          {formData.status === 'draft' && ticketsSold === 0 && (
            <Button variant="destructive" onClick={handleDelete}>
              <Trash2 className="h-4 w-4 mr-2" />
              Delete
            </Button>
          )}
        </div>
      </div>

      {/* Basic Information */}
      <Card className="glass border-white/10">
        <CardHeader>
          <CardTitle>Basic Information</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="title">Event Title *</Label>
            <Input
              id="title"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              className="glass border-white/10"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="glass border-white/10 min-h-[120px]"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="artist">Artist/Performer</Label>
              <Input
                id="artist"
                value={formData.artist}
                onChange={(e) => setFormData({ ...formData, artist: e.target.value })}
                className="glass border-white/10"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="category">Category</Label>
              <select
                id="category"
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                className="w-full h-10 px-3 rounded-md glass border border-white/10 bg-transparent text-foreground"
              >
                {categories.map((cat) => (
                  <option key={cat} value={cat} className="bg-background-paper">
                    {cat.charAt(0).toUpperCase() + cat.slice(1)}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="venue">Venue</Label>
            <Input
              id="venue"
              value={formData.venue}
              onChange={(e) => setFormData({ ...formData, venue: e.target.value })}
              className="glass border-white/10"
            />
          </div>
        </CardContent>
      </Card>

      {/* Date & Time */}
      <Card className="glass border-white/10">
        <CardHeader>
          <CardTitle>Date & Time</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="date">Start Date & Time *</Label>
              <Input
                id="date"
                type="datetime-local"
                value={formData.date}
                onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                className="glass border-white/10"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="end_date">End Date & Time</Label>
              <Input
                id="end_date"
                type="datetime-local"
                value={formData.end_date}
                onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                className="glass border-white/10"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Ticket Tiers */}
      <Card className="glass border-white/10">
        <CardHeader>
          <CardTitle>Ticket Tiers</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2 pb-4 border-b border-white/10">
            <Label>Currency</Label>
            <select
              value={formData.currency}
              onChange={(e) => setFormData({ ...formData, currency: e.target.value })}
              className="w-full md:w-48 h-10 px-3 rounded-md glass border border-white/10 bg-transparent text-foreground"
            >
              {currencies.map((curr) => (
                <option key={curr.code} value={curr.code} className="bg-background-paper">
                  {curr.symbol} {curr.code} - {curr.name}
                </option>
              ))}
            </select>
          </div>

          {ticketTiers.map((tier, index) => (
            <Card key={tier.type_id || index} className="glass border-white/10">
              <CardContent className="p-4 space-y-4">
                <div className="flex items-center justify-between">
                  <Badge>{tier.name}</Badge>
                  {tier.sold && tier.sold > 0 && (
                    <Badge variant="outline">{tier.sold} sold</Badge>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>Tier Name</Label>
                    <Input
                      value={tier.name}
                      onChange={(e) => updateTicketTier(index, 'name', e.target.value)}
                      className="glass border-white/10"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Price ({currencies.find(c => c.code === formData.currency)?.symbol || '$'})</Label>
                    <Input
                      type="number"
                      value={tier.price}
                      onChange={(e) => updateTicketTier(index, 'price', parseFloat(e.target.value))}
                      className="glass border-white/10"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Capacity</Label>
                    <Input
                      type="number"
                      value={tier.capacity}
                      onChange={(e) => updateTicketTier(index, 'capacity', parseInt(e.target.value))}
                      className="glass border-white/10"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Description</Label>
                  <Input
                    value={tier.description}
                    onChange={(e) => updateTicketTier(index, 'description', e.target.value)}
                    className="glass border-white/10"
                  />
                </div>
              </CardContent>
            </Card>
          ))}
        </CardContent>
      </Card>

      {/* Poster */}
      <Card className="glass border-white/10">
        <CardHeader>
          <CardTitle>Event Poster</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div 
            className={`relative border-2 border-dashed rounded-xl p-6 text-center transition-colors ${
              posterPreview ? 'border-primary' : 'border-white/20 hover:border-white/40'
            }`}
          >
            {posterPreview ? (
              <div className="relative">
                <img 
                  src={posterPreview} 
                  alt="Event poster preview" 
                  className="max-h-64 mx-auto rounded-lg object-cover"
                />
                <Button
                  variant="outline"
                  size="sm"
                  className="absolute top-2 right-2 glass"
                  onClick={() => {
                    setPosterPreview(null);
                    setFormData({ ...formData, poster_url: '' });
                  }}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ) : (
              <div 
                className="cursor-pointer"
                onClick={() => fileInputRef.current?.click()}
              >
                <div className="flex flex-col items-center space-y-3">
                  {isUploading ? (
                    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
                  ) : (
                    <div className="p-4 rounded-full bg-primary/10">
                      <ImageIcon className="h-8 w-8 text-primary" />
                    </div>
                  )}
                  <div>
                    <p className="text-foreground font-medium">
                      {isUploading ? 'Uploading...' : 'Click to upload poster image'}
                    </p>
                    <p className="text-sm text-gray-400 mt-1">
                      PNG, JPG, WebP or GIF (max 10MB)
                    </p>
                  </div>
                </div>
              </div>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handlePosterUpload}
              className="hidden"
            />
          </div>
        </CardContent>
      </Card>

      {/* Tags */}
      <Card className="glass border-white/10">
        <CardHeader>
          <CardTitle>Tags</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            <Label htmlFor="tags">Tags (comma-separated)</Label>
            <Input
              id="tags"
              value={formData.tags}
              onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
              placeholder="music, edm, dance, party"
              className="glass border-white/10"
            />
          </div>
        </CardContent>
      </Card>

      {/* Actions */}
      <div className="flex items-center justify-between pt-4">
        <Button variant="outline" onClick={() => navigate('/manage-events')} className="glass-hover">
          Cancel
        </Button>
        <div className="flex items-center space-x-3">
          <Button onClick={handleSave} disabled={isSaving} className="shadow-glow">
            <Save className="h-4 w-4 mr-2" />
            {isSaving ? 'Saving...' : 'Save Changes'}
          </Button>
          {formData.status === 'draft' && (
            <Button onClick={handlePublish} disabled={isSaving} variant="secondary">
              Publish Event
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
